import React, { useState } from 'react';

interface ManualSyncModalProps {
  onClose: () => void;
  onSync: (params: { source: string; startDate?: string; endDate?: string }) => void;
  sources: string[];
}

const ManualSyncModal: React.FC<ManualSyncModalProps> = ({ onClose, onSync, sources }) => {
  const [selectedSource, setSelectedSource] = useState('all');
  const [dateRange, setDateRange] = useState('last7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    let startDate: string | undefined;
    let endDate: string | undefined;
    
    if (dateRange === 'custom') {
      startDate = customStartDate;
      endDate = customEndDate;
    } else {
      const now = new Date();
      endDate = now.toISOString().split('T')[0];
      
      switch (dateRange) {
        case 'today':
          startDate = endDate;
          break;
        case 'yesterday':
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = yesterday.toISOString().split('T')[0];
          endDate = startDate;
          break;
        case 'last7days':
          const week = new Date(now);
          week.setDate(week.getDate() - 7);
          startDate = week.toISOString().split('T')[0];
          break;
        case 'last30days':
          const month = new Date(now);
          month.setDate(month.getDate() - 30);
          startDate = month.toISOString().split('T')[0];
          break;
        case 'last90days':
          const quarter = new Date(now);
          quarter.setDate(quarter.getDate() - 90);
          startDate = quarter.toISOString().split('T')[0];
          break;
        default:
          startDate = endDate;
      }
    }
    
    onSync({
      source: selectedSource,
      startDate,
      endDate
    });
    
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content manual-sync-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manual Sync</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="source">Source</label>
            <select
              id="source"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className="form-control"
            >
              <option value="all">All Sources</option>
              {sources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="dateRange">Date Range</label>
            <select
              id="dateRange"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="form-control"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last90days">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {dateRange === 'custom' && (
            <div className="date-range-inputs">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="form-control"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="form-control"
                  required
                />
              </div>
            </div>
          )}
          
          <div className="sync-info-message">
            <div className="info-icon">ℹ</div>
            <div className="info-text">
              <p><strong>What this does:</strong></p>
              <ul>
                <li>Fetches reviews from the selected source</li>
                <li>Processes them through AI for highlight extraction</li>
                <li>Updates the database with new reviews</li>
                <li>May take 2-10 minutes depending on volume</li>
              </ul>
            </div>
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Start Sync
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualSyncModal;